import React, { useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { 
  FolderIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { authAPI, exploreAPI } from '../services/api';
import ProjectCard from '../components/ProjectCard';
import { getImageUrl } from '../config/runtime';

const PublicUserProfile = () => {
  const { username } = useParams();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('activity'); // 'activity' (published projects) or 'created' (starters)
  const [searchTerm, setSearchTerm] = useState('');
  
  // Intersection observer ref for infinite scroll
  const observerTarget = useRef(null);

  // Fetch user data by username
  const { data: userResponse, isLoading: isUserLoading, error: userError } = useQuery({
    queryKey: ['public-user', username],
    queryFn: () => authAPI.getUserByUsername(username),
    enabled: !!username,
  });

  const user = userResponse?.data?.data;

  // Build query parameters for API call
  const buildQueryParams = (pageParam = 0) => {
    return {
      offset: pageParam,
      limit: 20, // Load 20 projects per page
    };
  };

  // Fetch user's activity (all public projects where user participated)
  const { 
    data: activityData,
    isLoading: isActivityLoading,
    isFetchingNextPage: isFetchingActivityNext,
    hasNextPage: hasActivityNext,
    fetchNextPage: fetchActivityNext,
    error: activityError,
  } = useInfiniteQuery({
    queryKey: ['user-activity-projects', user?.username],
    queryFn: ({ pageParam = 0 }) => authAPI.getPublicUserProjects(user?.username, buildQueryParams(pageParam)),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageProjects = lastPage?.data?.data || [];
      const requestedLimit = 20;
      
      // Stop immediately if we got an empty page
      if (lastPageProjects.length === 0) {
        return undefined;
      }
      
      if (lastPageProjects.length < requestedLimit) {
        return undefined;
      }
      
      // Safety check: stop after 10 pages
      const totalPages = allPages.length;
      if (totalPages >= 10) {
        return undefined;
      }
      
      const totalProjects = allPages.reduce((total, page) => total + ((page?.data?.data?.length) || 0), 0);
      return totalProjects;
    },
    enabled: !!user?.username,
    staleTime: 30000,
  });

  // Fetch user's created (explore) projects
  const { 
    data: createdData,
    isLoading: isCreatedLoading,
    isFetchingNextPage: isFetchingCreatedNext,
    hasNextPage: hasCreatedNext,
    fetchNextPage: fetchCreatedNext,
    error: createdError,
  } = useInfiniteQuery({
    queryKey: ['user-created-projects', user?.id],
    queryFn: ({ pageParam = 0 }) => exploreAPI.getAll({
      ...buildQueryParams(pageParam),
      createdBy: user?.id, // Filter by creator
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageProjects = lastPage?.data?.data?.projects || [];
      const requestedLimit = 20;
      
      // Stop immediately if we got an empty page
      if (lastPageProjects.length === 0) {
        return undefined;
      }
      
      if (lastPageProjects.length < requestedLimit) {
        return undefined;
      }
      
      // Safety check: stop after 10 pages
      const totalPages = allPages.length;
      if (totalPages >= 10) {
        return undefined;
      }
      
      const totalProjects = allPages.reduce((total, page) => total + (page?.data?.data?.projects?.length || 0), 0);
      return totalProjects;
    },
    enabled: !!user?.id,
    staleTime: 30000,
  });

  // Flatten all pages into single arrays
  const allActivityProjects = React.useMemo(() => {
    if (!activityData?.pages) return [];
    return activityData.pages.reduce((acc, page) => {
      const projects = Array.isArray(page?.data?.data) ? page.data.data : [];
      return [...acc, ...projects];
    }, []);
  }, [activityData?.pages]);
  
  const allCreatedProjects = React.useMemo(() => {
    if (!createdData?.pages) return [];
    return createdData.pages.reduce((acc, page) => {
      const projects = Array.isArray(page?.data?.data?.projects) ? page.data.data.projects : [];
      return [...acc, ...projects];
    }, []);
  }, [createdData?.pages]);
  
  // Filter to only show projects that the user made public/published (owned by user)
  // Deduplicate across pages to avoid offset/updatedAt race duplicates
  const userActivityProjects = React.useMemo(() => {
    if (!user?.id) return [];
    return (allActivityProjects || [])
      .filter(project => project.ownerId === user.id) // Only show projects owned by the user
      .filter((project, index, array) =>
        array.findIndex(p => p.id === project.id) === index
      );
  }, [allActivityProjects, user?.id]);

  // Backend already filters created projects by user, just ensure they're explore type
  const userCreatedProjects = React.useMemo(() => 
    allCreatedProjects.filter(project => 
      project.projectType === 'explore'
    ),
    [allCreatedProjects]
  );

  // Filter projects based on search and active tab
  const displayProjects = React.useMemo(() => {
    const sourceProjects = activeTab === 'activity' ? userActivityProjects : userCreatedProjects;
    return sourceProjects.filter(project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.tags && project.tags.some(tag => 
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      ))
    );
  }, [userActivityProjects, userCreatedProjects, activeTab, searchTerm]);

  // Intersection Observer for infinite scroll
  const observeLastProject = useCallback(
    (node) => {
      const isLoading = activeTab === 'activity' ? isActivityLoading : isCreatedLoading;
      const isFetchingNext = activeTab === 'activity' ? isFetchingActivityNext : isFetchingCreatedNext;
      const hasNext = activeTab === 'activity' ? hasActivityNext : hasCreatedNext;
      const fetchNext = activeTab === 'activity' ? fetchActivityNext : fetchCreatedNext;
      
      if (isLoading || isFetchingNext || !hasNext) return;
      if (observerTarget.current) observerTarget.current.disconnect();
      
      observerTarget.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNext && !isFetchingNext) {
          fetchNext();
        }
      }, {
        threshold: 0.1, // Use threshold for more reliable triggering
      });
      
      if (node) observerTarget.current.observe(node);
    },
    [activeTab, isActivityLoading, isCreatedLoading, isFetchingActivityNext, isFetchingCreatedNext, hasActivityNext, hasCreatedNext, fetchActivityNext, fetchCreatedNext]
  );

  const getDisplayName = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user?.firstName) {
      return user.firstName;
    } else if (user?.username) {
      return user.username;
    }
    return 'User';
  };

  const getInitials = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    } else if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    } else if (user?.username) {
      return user.username[0].toUpperCase();
    }
    return 'U';
  };

  if (isUserLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="px-4 py-6 sm:px-0">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow h-96"></div>
          </div>
        </div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">User Not Found</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                The user profile you're looking for doesn't exist.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isProjectsError = activeTab === 'activity' ? !!activityError : !!createdError;
  const isProjectsLoading = activeTab === 'activity' ? isActivityLoading : isCreatedLoading;
  const isFetchingNextPage = activeTab === 'activity' ? isFetchingActivityNext : isFetchingCreatedNext;

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 pt-0">
        <div className="px-6 pt-6 pb-24 md:pb-6 sm:px-0">

        {/* Profile Header */}
        <div className="pb-2">
          {/* User Info Section */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                {user?.avatar ? (
                  <img src={getImageUrl(user.avatar)} alt="" className="h-20 w-20 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-2xl">{getInitials(user)}</span>
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{getDisplayName(user)}</h1>
                <p className="text-lg text-gray-600 dark:text-gray-300">@{user?.username}</p>
                {user?.createdAt && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Member since {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* User Stats */}
          {/* <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{userCompletedProjects.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{userCreatedProjects.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Created</div>
            </div>
          </div> */}

          {/* User Interests */}
          {user?.interests && (
            <div className="mb-6">
              {/* <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">About</h3> */}
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                {user.interests}
              </p>
            </div>
          )}
        </div>

        {/* Tabs and Projects Section */}
        <div className="mt-8">
          {/* Tabs Header */}
          <div className="flex items-center gap-4 sm:gap-8 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button
              onClick={() => setActiveTab('activity')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'activity'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="hidden sm:inline">Published Projects</span>
              <span className="sm:hidden">Published</span>
            </button>
            <button
              onClick={() => setActiveTab('created')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'created'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="hidden sm:inline">Project Starters</span>
              <span className="sm:hidden">Starters</span>
            </button>
          </div>

          {/* Search */}
          <div className="my-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Projects Grid */}
          {isProjectsLoading && displayProjects.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : isProjectsError ? (
            <div className="text-center py-12">
              <div className="text-red-500 dark:text-red-400 mb-4">
                Error loading projects
              </div>
            </div>
          ) : displayProjects.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayProjects.map((project, index) => {
                  const isLast = index === displayProjects.length - 1;
                  return (
                    <div
                      key={project.id}
                      ref={isLast ? observeLastProject : null}
                    >
                      <ProjectCard
                        project={project}
                        navigateTo={activeTab === 'activity' ? 'projects' : 'starters'}
                        preferCapturedCover={activeTab === 'activity'}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Loading indicator for fetching next page */}
              {isFetchingNextPage && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {[...Array(3)].map((_, i) => (
                    <div key={`loading-${i}`} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-4"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                <FolderIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm 
                  ? `No ${activeTab === 'activity' ? 'published projects' : 'starters'} found` 
                  : activeTab === 'activity'
                    ? 'No published projects yet'
                    : 'No starters yet'
                }
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto">
                {searchTerm 
                  ? 'Try adjusting your search terms or browse all projects.' 
                  : activeTab === 'activity' 
                    ? 'This user hasn\'t published any public projects yet.'
                    : 'This user hasn\'t created any project starters yet.'
                }
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default PublicUserProfile;
